import { EnvironmentOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons"
import { Alert, Avatar, Button, Empty, Skeleton, Tag } from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { friendlyContactRole } from "./helpers/contact-role.ts"
import { formatPortalDate } from "./helpers/format-date.ts"
import { buildMilestoneSchedule } from "./helpers/milestones.ts"
import { fetchPortalProjectDetail } from "./redux/portal.thunks.ts"
import type { PortalProjectPhase } from "./types/portal.types.ts"

const MILESTONE_STATUS_LABEL_KEY: Record<number, string> = {
  1: "portal.project.milestoneStatus.pending",
  2: "portal.project.milestoneStatus.inProgress",
  3: "portal.project.milestoneStatus.completed",
  4: "portal.project.milestoneStatus.blocked"
}

function MilestoneRow({
  phase,
  isOverdue,
  t
}: {
  phase: PortalProjectPhase
  isOverdue: boolean
  t: (key: string) => string
}) {
  const date = formatPortalDate(phase.end_date || phase.start_date)
  const statusKey = MILESTONE_STATUS_LABEL_KEY[phase.status]

  return (
    <li className="flex flex-col gap-1 border-b border-gray-50 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-medium text-gray-800">{phase.name}</p>
        {date && (
          <p className="text-xs text-gray-500">
            {t("portal.home.estimatedOn")} {date}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        {isOverdue && (
          <Tag color="orange">{t("portal.project.scheduleUpdateNeeded")}</Tag>
        )}
        {!isOverdue && statusKey && <Tag>{t(statusKey)}</Tag>}
      </div>
    </li>
  )
}

function PortalProjectPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const selectedProjectId = useAppSelector(
    (state) => state.portal.selectedProjectId
  )
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const fetching = useAppSelector((state) => state.portal.projectDetailFetching)
  const failed = useAppSelector((state) => state.portal.projectDetailError)

  useEffect(() => {
    if (selectedProjectId) void dispatch(fetchPortalProjectDetail(selectedProjectId))
  }, [dispatch, selectedProjectId])

  const schedule = useMemo(
    () => buildMilestoneSchedule(detail?.project_phases ?? []),
    [detail?.project_phases]
  )

  const milestonesByType = useMemo(() => {
    const types = detail?.project_type_details ?? []
    const phases = detail?.project_phases ?? []
    const groups = types.map((type) => ({
      type,
      phases: phases.filter((phase) => phase.project_type === type.id)
    }))
    const ungrouped = phases.filter(
      (phase) => !types.some((type) => type.id === phase.project_type)
    )
    if (ungrouped.length > 0) {
      groups.push({ type: { id: "none", name: t("portal.project.otherMilestones") }, phases: ungrouped })
    }
    return groups
  }, [detail?.project_type_details, detail?.project_phases, t])

  const gcContact = useMemo(() => {
    const managers = detail?.project_managers ?? []
    return managers.find((m) => m.is_main) ?? managers[0] ?? null
  }, [detail?.project_managers])

  if (!selectedProjectId) {
    return (
      <PortalPageContainer>
        <CardComponent>
          <Empty description={t("portal.project.noProject")} />
        </CardComponent>
      </PortalPageContainer>
    )
  }

  if (fetching && !detail) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Skeleton active paragraph={{ rows: 8 }} />
      </PortalPageContainer>
    )
  }

  if (failed || !detail) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Alert
          type="error"
          showIcon
          message={t("portal.project.loadError")}
          action={
            <Button
              size="small"
              onClick={() => void dispatch(fetchPortalProjectDetail(selectedProjectId))}
            >
              {t("portal.home.retry")}
            </Button>
          }
        />
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer className="flex max-w-3xl flex-col gap-4">
      <CardComponent styles={{ body: { padding: 20 } }}>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-left text-[22px] font-semibold text-gray-900">{detail.lead_name}</h1>
            {detail.address && (
              <p className="flex items-center gap-1 text-sm text-gray-500">
                <EnvironmentOutlined />
                {[detail.address, detail.city, detail.state, detail.zip_code]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
          </div>
          {detail.project_status_name && <Tag color="blue">{detail.project_status_name}</Tag>}
        </div>

        {detail.scope && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-xs uppercase text-gray-400">{t("portal.project.scope")}</p>
            <p className="mt-1 text-sm text-gray-700">{detail.scope}</p>
          </div>
        )}
      </CardComponent>

      {gcContact && (
        <CardComponent title={t("portal.home.gcContact")} size="small">
          <div className="flex items-center gap-3">
            <Avatar src={gcContact.avatar || undefined} size={48}>
              {gcContact.first_name?.[0]}
            </Avatar>
            <div>
              <p className="font-medium text-gray-900">
                {[gcContact.first_name, gcContact.last_name].filter(Boolean).join(" ")}
              </p>
              <p className="text-xs text-gray-500">{friendlyContactRole(gcContact.role)}</p>
              <div className="flex flex-col gap-0.5 text-sm text-gray-500 sm:flex-row sm:gap-4">
                {gcContact.email && (
                  <a
                    href={`mailto:${gcContact.email}`}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    <MailOutlined />
                    {gcContact.email}
                  </a>
                )}
                {gcContact.phone && (
                  <a
                    href={`tel:${gcContact.phone}`}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    <PhoneOutlined />
                    {gcContact.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardComponent>
      )}

      {milestonesByType.map((group) => (
        <CardComponent key={group.type.id} title={group.type.name} size="small">
          {group.phases.length === 0 ? (
            <p className="text-sm text-gray-400">{t("portal.project.noMilestonesForType")}</p>
          ) : (
            <ul>
              {group.phases.map((phase) => (
                <MilestoneRow
                  key={phase.id}
                  phase={phase}
                  isOverdue={schedule.overdue.some((m) => m.phase.id === phase.id)}
                  t={t}
                />
              ))}
            </ul>
          )}
        </CardComponent>
      ))}

      {milestonesByType.length === 0 && (
        <CardComponent title={t("portal.project.milestones")} size="small">
          <p className="text-sm text-gray-400">{t("portal.project.noMilestonesYet")}</p>
        </CardComponent>
      )}
    </PortalPageContainer>
  )
}

export default PortalProjectPage
